import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { SimpleTableHeadColumn } from '../configure/configuration-files/types';
import useStyles from './style';

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((element, index) => [element, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((element) => element[0]);
}

const SimpleTableCustomCell = ({
  rows,
  headCells,
  maxHeight,
  initialSort,
  initialSortDirection,
  id,
}: {
  rows: { [key: string]: string | number }[];
  headCells: SimpleTableHeadColumn[];
  maxHeight?: number;
  initialSort?: string;
  initialSortDirection?: Order;
  id?: string;
}) => {
  const [order, setOrder] = useState<Order>(initialSortDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string | undefined>(initialSort);
  const classes = useStyles();

  const handleRequestSort = (accessor: string) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(accessor);
  };

  const TableRowCustomized = (property: any) => (
    <TableRow className={classes.root}>
      {headCells.map((headCell: SimpleTableHeadColumn, index: number) => {
        const value = property.row[headCell.accessor];
        if (headCell.customCellComponent)
          return (
            <TableCell>
              <headCell.customCellComponent
                id={`${id}-${index}`}
                row={property.row}
                disabled={headCell.disabled}
                callback={headCell.callback}
              />
            </TableCell>
          );
        return <TableCell key={property.row[headCell.accessor]}>{value}</TableCell>;
      })}
    </TableRow>
  );

  return (
    <>
      <TableContainer id={id} style={{ maxHeight: `${maxHeight}px` }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((headCell: SimpleTableHeadColumn) => (
                <TableCell key={headCell.label}>
                  <TableSortLabel
                    active={orderBy === headCell.accessor}
                    direction={orderBy === headCell.accessor ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.accessor)}
                    IconComponent={ExpandMore}
                  >
                    <Typography variant="h6">{headCell.label}</Typography>
                    {orderBy === headCell.accessor ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {orderBy
              ? stableSort(rows, getComparator(order, orderBy)).map(
                  (row: { [key: string]: string } | any) => <TableRowCustomized row={row} />,
                )
              : rows.map((row: any) => <TableRowCustomized row={row} />)}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SimpleTableCustomCell;
